import { log } from './log';
import { Auth } from './cognito';

const LOG_CHANNEL = 'realtime';

type RealtimeOptions = {
  REALTIME_URL: string | undefined;
};

const RealtimeEvents = {
  CONNECT: 'connect',
  CONNECTED: 'connected',
  RECONNECT: 'reconnected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
  FILE_CHANGED: 'file:changed',
};

class RealtimeClient {
  accountId?: string;
  REALTIME_URL: string | undefined;
  webSocket?: WebSocket;
  attempts: number = 0;
  maxAttempts: number = 10;
  timeout: number = 10000;
  connected: boolean = false;
  connecting: boolean = false;
  listeners: {
    [event: string]: Array<(T: any) => any>;
  };
  constructor({ REALTIME_URL }: RealtimeOptions) {
    this.REALTIME_URL = REALTIME_URL;
    this.listeners = {};
  }
  async connect(accountId?: string) {
    if (!this.REALTIME_URL) return;
    if (this.connected || this.connecting) return;
    this.accountId = accountId;
    this.connecting = true;
    log(LOG_CHANNEL, 'connecting to ws', Auth, accountId);
    const token = await Auth.getJwtToken();
    const webSocket = new WebSocket(
      this.REALTIME_URL + '?auth=' + token + '&accountId=' + accountId,
    );
    webSocket.onopen = this.onOpen;
    webSocket.onmessage = this.onMessage;
    webSocket.onclose = this.onClose;
    webSocket.onerror = this.onError;
    this.webSocket = webSocket;
    this.connected = true;
    this.connecting = false;
    this.emit(RealtimeEvents.CONNECT);
  }
  disconnect() {
    this.connected = false;
    if (this.webSocket) {
      this.webSocket.onopen = null;
      this.webSocket.onclose = null;
      this.webSocket.onmessage = null;
      this.webSocket.onerror = null;
      this.webSocket = undefined;
    }
  }
  async reconnect(event: Event) {
    this.disconnect();
    if (this.attempts >= this.maxAttempts) {
      log(LOG_CHANNEL, 'max reconnection attempts reached');
      return;
    }
    this.attempts++;
    const timeout = this.timeout * this.attempts;
    log(LOG_CHANNEL, 'schedule reconnect', this.attempts, timeout);

    setTimeout(async () => {
      this.emit(RealtimeEvents.RECONNECT);
      log(LOG_CHANNEL, 'reconnecting', this.attempts);
      await this.connect(this.accountId);
    }, timeout);
  }
  emit<T = any>(event: string, data?: T) {
    (this.listeners[event] || []).forEach((fn) => fn(data));
  }
  on<T = any>(event: string, accountId: string, listener: (data: T) => any) {
    if (!this.connected || this.accountId !== accountId)
      this.connect(accountId);
    this.listeners[event] = this.listeners[event] || [];
    this.listeners[event].push(listener);
  }
  off<T = any>(event: string, listener: (data: T) => any) {
    if (listener) {
      this.listeners[event] = (this.listeners[event] || []).filter(
        (fn) => fn !== listener,
      );
    } else {
      this.listeners[event] = [];
    }
  }
  onMessage = (messageEvent: MessageEvent) => {
    console.log('socket message', messageEvent);
    log(LOG_CHANNEL, messageEvent);
    const json = JSON.parse(messageEvent.data);
    const { event, data } = json;
    log(LOG_CHANNEL, 'emit', event, data);
    this.emit(event, data);
  };
  onOpen = (event: Event) => {
    this.attempts = 0;

    log(LOG_CHANNEL, 'connected', this.accountId);
    this.emit<Event>(RealtimeEvents.CONNECTED, event);
  };
  onClose = (event: CloseEvent) => {
    log(LOG_CHANNEL, 'disconnected');
    this.emit<CloseEvent>(RealtimeEvents.DISCONNECTED, event);
    if (event.code !== 1e3 && event.code !== 1005) this.reconnect(event);
  };
  onError = (event: Event & { code?: string }) => {
    log(LOG_CHANNEL, 'error', event);
    this.emit<Event>(RealtimeEvents.ERROR, event);
    if (event && event.code === 'ECONNREFUSED') this.reconnect(event);
  };
}
export let Realtime: RealtimeClient;
export const initRealtime = ({ REALTIME_URL }: RealtimeOptions) => {
  if (!Realtime) {
    Realtime = new RealtimeClient({ REALTIME_URL });
    //Realtime.connect()
  }
  return Realtime;
};
